export type ReportFilterOp = "eq" | "neq" | "in" | "gte" | "lte" | "contains";

export interface ReportFilter {
  field: string;
  op: ReportFilterOp;
  value: unknown;
}

const ALLOWED_OPS = new Set<ReportFilterOp>([
  "eq", "neq", "in", "gte", "lte", "contains",
]);

function isReportFilter(value: unknown): value is ReportFilter {
  if (!value || typeof value !== "object") return false;
  const filter = value as Record<string, unknown>;
  return typeof filter.field === "string"
    && typeof filter.op === "string"
    && ALLOWED_OPS.has(filter.op as ReportFilterOp)
    && "value" in filter;
}

export function parseReportFilters(filterJson: string): ReportFilter[] {
  try {
    const parsed = JSON.parse(filterJson) as { filters?: unknown };
    return Array.isArray(parsed.filters)
      ? parsed.filters.filter(isReportFilter)
      : [];
  } catch {
    return [];
  }
}

export function filterValueToString(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(",");
  if (value === null || value === undefined) return "";
  return String(value);
}

export interface ReportViewDealsProps {
  dealTypeId?: string;
  ownerId?: string;
  status?: string;
  q?: string;
  customFilters: ReportFilter[];
}

export function reportViewToDealsProps(filterJson: string): ReportViewDealsProps {
  const filters = parseReportFilters(filterJson);
  const valueOf = (field: string): string | undefined => {
    const filter = filters.find((item) => item.field === field);
    return filter ? filterValueToString(filter.value) || undefined : undefined;
  };

  return {
    dealTypeId: valueOf("dealTypeId"),
    ownerId: valueOf("ownerId"),
    status: valueOf("status"),
    q: valueOf("q"),
    customFilters: filters.filter((item) =>
      item.field.startsWith("customFields."),
    ),
  };
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (typeof left === "number" && typeof right === "number") return left === right;
  return String(left).toLowerCase() === String(right).toLowerCase();
}

function compareOrdered(left: unknown, right: unknown): number {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }

  const leftDate = Date.parse(String(left));
  const rightDate = Date.parse(String(right));
  if (!Number.isNaN(leftDate) && !Number.isNaN(rightDate)) {
    return leftDate - rightDate;
  }

  return 0;
}

export function dealMatchesCustomFilters(
  customFields: Record<string, unknown> | undefined,
  filters: ReportFilter[],
): boolean {
  return filters.every((filter) => {
    const name = filter.field.slice("customFields.".length);
    const actual = customFields?.[name];

    if (actual === undefined || actual === null) return filter.op === "neq";

    switch (filter.op) {
      case "eq":
        return valuesEqual(actual, filter.value);
      case "neq":
        return !valuesEqual(actual, filter.value);
      case "contains":
        return String(actual).toLowerCase().includes(String(filter.value).toLowerCase());
      case "in":
        return Array.isArray(filter.value)
          && filter.value.some((item) => valuesEqual(actual, item));
      case "gte":
        return compareOrdered(actual, filter.value) >= 0;
      case "lte":
        return compareOrdered(actual, filter.value) <= 0;
      default:
        return false;
    }
  });
}
